/**
 * Mirror of server/utils/wizardV2Progress.js for the backoffice.
 *
 * Keeps step inference consistent between the API and the admin UI so the
 * list page and the detail page can compute the same values without an
 * extra round-trip. Keep in sync with the server copy — both read from the
 * same wizard_sessions.context.extractedInfo shape written by
 * server/routes/wizardV2.js, and both use the order from
 * client/src/wizard-v2/stepConfig.js STEP_LIST.
 */

export const STEP_ORDER = [
  'initial-prompt',
  'website-type',
  'business-name',
  'audience',
  'design',
  'logo',
  'seo',
];

export const STEP_LABELS = {
  'initial-prompt': 'Initial prompt',
  'website-type': 'Website type',
  'business-name': 'Business name',
  audience: 'Audience',
  design: 'Design',
  logo: 'Logo',
  seo: 'SEO keywords',
  completed: 'Completed',
};

export const STEP_HELPERS = {
  'initial-prompt': 'Tell us about your site',
  'website-type': 'What kind of website are you building?',
  'business-name': "What's the name of your business?",
  audience: "Who's the site for?",
  design: 'Pick a design direction',
  logo: 'Generate or upload a logo',
  seo: 'Pick the SEO keywords',
};

export const COUNTED_STEP_COUNT = STEP_ORDER.length - 1; // initial-prompt isn't counted in "X of N"

function nonEmptyArray(value) {
  return Array.isArray(value) && value.length > 0;
}

export function isV2Session(row) {
  if (!row) return false;
  const ei = (row.context && row.context.extractedInfo) || {};
  return ei.wizardVersion === 'v2' || ei.useAIDesignsV2 === true;
}

export function inferWizardV2Progress(row) {
  if (!row) {
    return { version: 'v1', stepsCompleted: 0, currentStep: null, completedSteps: [] };
  }

  const ctx = row.context || {};
  const ei = ctx.extractedInfo || {};

  if (!isV2Session(row)) {
    return { version: 'v1', stepsCompleted: 0, currentStep: null, completedSteps: [] };
  }

  const completedSteps = [];
  if (ctx.initialPrompt || ei.initialPrompt) completedSteps.push('initial-prompt');
  if (ei.siteType) completedSteps.push('website-type');
  if (ei.companyName || ei.businessName) completedSteps.push('business-name');
  if (
    (ei.targetAudience &&
      (typeof ei.targetAudience !== 'object' || Object.keys(ei.targetAudience).length > 0)) ||
    nonEmptyArray(ei.audiences)
  ) {
    completedSteps.push('audience');
  }
  if (nonEmptyArray(ei.selectedDribbbleDesigns)) completedSteps.push('design');
  if (ei.selectedLogo) completedSteps.push('logo');
  if (nonEmptyArray(ei.seoKeywords)) completedSteps.push('seo');

  let currentStep;
  if (row.completed) {
    currentStep = 'completed';
  } else {
    const lastIdx = completedSteps.reduce(
      (max, step) => Math.max(max, STEP_ORDER.indexOf(step)),
      -1
    );
    currentStep = lastIdx + 1 < STEP_ORDER.length ? STEP_ORDER[lastIdx + 1] : 'seo';
  }

  return {
    version: 'v2',
    stepsCompleted: completedSteps.length,
    currentStep,
    completedSteps,
  };
}

export function describeAudience(targetAudience) {
  if (!targetAudience) return null;
  if (typeof targetAudience === 'string') {
    const trimmed = targetAudience.trim();
    if (!trimmed) return null;
    return { label: trimmed, labels: [trimmed], otherText: null, custom: 0, count: 1 };
  }
  const selected = Array.isArray(targetAudience.selectedAudiences)
    ? targetAudience.selectedAudiences
    : [];
  const otherText = (targetAudience.otherText || '').toString().trim();
  const labels = selected.map((a) => a.label || a.name || a.id).filter(Boolean);
  const customCount = otherText ? 1 : 0;
  return {
    labels,
    otherText: otherText || null,
    count: labels.length + customCount,
    custom: customCount,
  };
}

export function describeDesign(extractedInfo) {
  const designs = Array.isArray(extractedInfo?.selectedDribbbleDesigns)
    ? extractedInfo.selectedDribbbleDesigns
    : [];
  if (designs.length === 0) return null;
  const selected = designs[0];
  return {
    selected,
    selectedImage:
      selected?.imageUrl || selected?.screenshot_url || selected?.thumbnail || selected?.image,
    selectedTitle: selected?.title || selected?.name || null,
  };
}

export function describeLogo(extractedInfo) {
  if (!extractedInfo?.selectedLogo) return null;
  const logo = extractedInfo.selectedLogo;
  return {
    url: logo.url || logo.imageUrl || logo.dataUrl || null,
    source: logo.source || (logo.url && logo.url.startsWith('data:') ? 'upload' : 'ai'),
    label: logo.label || null,
  };
}

export function describeSeoKeywords(extractedInfo) {
  const keywords = Array.isArray(extractedInfo?.seoKeywords) ? extractedInfo.seoKeywords : [];
  return keywords
    .map((k) => {
      if (typeof k === 'string') return k;
      return k?.keyword || k?.label || k?.value || null;
    })
    .filter(Boolean);
}

export function getSiteTypeLabel(siteType) {
  if (!siteType) return null;
  const map = {
    landing_page: 'Landing page',
    marketing: 'Marketing site',
    catalog: 'Catalog',
    ecommerce: 'E-commerce',
  };
  return map[siteType] || siteType;
}
